/**
 * useJesseConversation — Drop-in replacement for @elevenlabs/react useConversation()
 *
 * Uses the Jesse backend WebSocket pipeline:
 *   Browser mic → MediaRecorder → WS → Backend (Whisper STT → Azure GPT → ElevenLabs/Parkiet TTS)
 *
 * Interface matches useConversation() from @elevenlabs/react:
 *   - startSession() → starts mic capture + WebSocket
 *   - endSession()   → stops mic + closes WebSocket
 *   - setMicMuted()  → pause/resume mic
 *   - status         → 'disconnected' | 'connecting' | 'connected'
 *   - isSpeaking     → boolean, true when TTS audio is playing
 *
 * Enable with: REACT_APP_USE_OWN_PIPELINE=true
 *
 * Auth flow:
 *   Browsers cannot send Authorization headers on WebSocket upgrade requests.
 *   We exchange the Auth0 JWT for a single-use 60s opaque token via GET /api/ws-token,
 *   then append it as ?token=<value> to the ws:// URL. The backend invalidates it
 *   immediately after accepting the connection.
 *
 * @param {object} options
 * @param {function} options.getAccessToken  - Async fn returning the Auth0 JWT string
 * @param {function} [options.onMessage]     - Called with { source, message, isFinal, isDelta }
 * @param {function} [options.onModeChange]  - Called with { mode: 'listening'|'speaking' }
 * @param {function} [options.onError]       - Called with { message: string }
 */
import { useRef, useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const WS_URL  = API_URL.replace(/^http/, 'ws');

export function useJesseConversation({ getAccessToken, onMessage, onModeChange, onError } = {}) {
  const [status, setStatus]       = useState('disconnected');
  const [isSpeaking, setIsSpeaking] = useState(false);

  const wsRef        = useRef(null);
  const recorderRef  = useRef(null);
  const streamRef    = useRef(null);
  const audioCtxRef  = useRef(null);
  const isMutedRef   = useRef(false);

  // Play base64-encoded audio received from backend
  const playAudio = useCallback(async (base64data) => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;

      const bytes  = Uint8Array.from(atob(base64data), c => c.charCodeAt(0));
      const buffer = await ctx.decodeAudioData(bytes.buffer);

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);

      setIsSpeaking(true);
      onModeChange?.({ mode: 'speaking' });

      source.onended = () => {
        setIsSpeaking(false);
        onModeChange?.({ mode: 'listening' });
      };

      source.start(0);
    } catch (err) {
      console.error('Audio playback error:', err);
      setIsSpeaking(false);
    }
  }, [onModeChange]);

  const startSession = useCallback(async () => {
    try {
      setStatus('connecting');

      // Exchange JWT for a single-use WebSocket token.
      // Browsers cannot send Authorization headers on WS upgrade requests,
      // so we get a short-lived opaque token first.
      let wsUrl = `${WS_URL}/ws`;
      if (getAccessToken) {
        const jwt = await getAccessToken();
        const tokenRes = await fetch(`${API_URL}/api/ws-token`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (!tokenRes.ok) {
          throw new Error(`WS token exchange mislukt (${tokenRes.status})`);
        }
        const { token } = await tokenRes.json();
        wsUrl = `${WS_URL}/ws?token=${token}`;
      }

      // Get mic access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Connect WebSocket to backend
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('connected');
        onModeChange?.({ mode: 'listening' });

        // Start sending audio chunks
        const recorder = new MediaRecorder(stream, {
          mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
            ? 'audio/webm;codecs=opus'
            : 'audio/webm',
        });
        recorderRef.current = recorder;

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0 && ws.readyState === WebSocket.OPEN && !isMutedRef.current) {
            // Convert Blob to base64 and send as audio message
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64 = reader.result.split(',')[1];
              ws.send(JSON.stringify({ type: 'audio', data: base64 }));
            };
            reader.readAsDataURL(event.data);
          }
        };

        // Send audio chunks every 250ms for low latency
        recorder.start(250);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          switch (msg.type) {
            case 'stt_partial':
              // Partial transcription — show live caption
              onMessage?.({ source: 'user', message: msg.text, isFinal: false });
              break;

            case 'stt_final':
              // Final transcription
              onMessage?.({ source: 'user', message: msg.text, isFinal: true });
              break;

            case 'assistant_stream':
              // LLM streaming delta
              onMessage?.({ source: 'assistant', message: msg.delta, isDelta: true });
              break;

            case 'assistant_done':
              // Full assistant response
              onMessage?.({ source: 'assistant', message: msg.text, isFinal: true });
              break;

            case 'audio':
              // TTS audio — play it
              playAudio(msg.data);
              break;

            default:
              break;
          }
        } catch (err) {
          console.error('WS message parse error:', err);
        }
      };

      ws.onerror = (err) => {
        console.error('WebSocket error:', err);
        onError?.({ message: 'Verbinding mislukt. Probeer opnieuw.' });
        setStatus('disconnected');
      };

      ws.onclose = () => {
        setStatus('disconnected');
        setIsSpeaking(false);
      };

    } catch (err) {
      console.error('startSession error:', err);
      onError?.({ message: err.message });
      setStatus('disconnected');
    }
  }, [playAudio, onModeChange, onMessage, onError]);

  const endSession = useCallback(() => {
    recorderRef.current?.stop();
    streamRef.current?.getTracks().forEach(t => t.stop());
    wsRef.current?.close();
    audioCtxRef.current?.close();

    recorderRef.current  = null;
    streamRef.current    = null;
    wsRef.current        = null;
    audioCtxRef.current  = null;

    setStatus('disconnected');
    setIsSpeaking(false);
  }, []);

  const setMicMuted = useCallback((muted) => {
    isMutedRef.current = muted;
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach(t => { t.enabled = !muted; });
    }
  }, []);

  return { startSession, endSession, setMicMuted, status, isSpeaking };
}
